import { Button, Container, Grid, Paper, Stack, Typography } from '@mui/material';

import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';

import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ActivityEnum, AdminSubactivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { CourseOfferingApi, ICourseOffering } from '../api/CourseOfferingApi';
import { UserApi } from '../api/UserApi';
import CourseOfferingForm from '../component/CourseOfferingForm';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';

export default function CourseOffering() {

    useUserActivityTracker(ActivityEnum.Admin, AdminSubactivityEnum.CourseOffering)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [courseOfferingList, setCourseOfferingList] = React.useState<ICourseOffering[]>([]);
    const [selectedCourseOffering, setSelectedCourseOffering] = React.useState<ICourseOffering | null>(null);

    const [courseOfferingIsAdding, setCourseOfferingIsAdding] = React.useState(false);

    React.useEffect(() => {
        UserApi.assertAdmin()
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })

        CourseOfferingApi.getAll()
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to CourseOfferingApi.getAll() " + response.status)
            })
            .then(list => setCourseOfferingList(list))
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [history, location, setErrorMessage]);

    /*
        COURSE OFFERING HANDLERS
     */

    const handleAddCourseOffering = async () => {
        setCourseOfferingIsAdding(true)
        const newCourseOffering: ICourseOffering = {
            id: 0,
            course_id: null,
            name: 'New Course Offering Name',
            description: null,
            start_date: null,
            end_date: null,
            display_conversation_menu: true,
            display_office_hour_menu: true
        };
        CourseOfferingApi.create(newCourseOffering)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return null
            })
            .then(newCourseOfferingWithId => {
                setSelectedCourseOffering(newCourseOfferingWithId)
                // setSelectedDialog(null)
                // setSelectedDialogPrompt(null)
                CourseOfferingApi.getAll().then(response => {
                    setCourseOfferingList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setCourseOfferingIsAdding(false)
            })
    };

    const handleCourseOfferingMenuClick = (courseOffering: ICourseOffering) => {
        setSelectedCourseOffering(courseOffering)
    };

    const handleCourseOfferingFormSubmit = async (updatedCourseOffering: ICourseOffering) => {
        CourseOfferingApi.update(updatedCourseOffering)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .then(() => {
                CourseOfferingApi.getAll().then(response => {
                    setCourseOfferingList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    const handleDeleteCourseOffering = async (courseOfferingId: number) => {
        CourseOfferingApi.delete(courseOfferingId)
            .then(response => {
                if (response.status < 300) {
                    return response.data
                }
                console.error(response)
                handleErrorMessage(response, setErrorMessage, history, location);
                return []
            })
            .then(() => {
                setSelectedCourseOffering(null)
                CourseOfferingApi.getAll().then(response => {
                    setCourseOfferingList(response.data)
                })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }

    return (
        <Container sx={{ mx: 2, my: 2 }} maxWidth={false}>
            <Grid container justifyContent="center">
                {/* <Grid item sm={12} md={6} alignItems="center" justifyContent="center" sx={{ marginTop: 2 }}> */}
                {/* Left Grid with fixed width */}
                <Grid item sx={{ width: '480px', marginTop: 2 }}>
                    <Stack spacing={2}>
                        {courseOfferingList.map(dialogPlan => (<div key={dialogPlan.id}>
                            <Stack direction="row" alignItems="center">
                                <MenuBookRoundedIcon sx={{ mr: 2, color: selectedCourseOffering && selectedCourseOffering.id === dialogPlan.id ? '#FFBE19' : 'inherit' }} />
                                <Typography onClick={() => handleCourseOfferingMenuClick(dialogPlan)}>
                                    {dialogPlan.name}
                                </Typography>
                            </Stack>
                        </div>))}
                        <Button variant="outlined" onClick={handleAddCourseOffering} disabled={courseOfferingIsAdding} sx={{ width: 'fit-content', ml: 4 }}>
                            Add Course Offering
                        </Button>
                    </Stack>
                </Grid>
                {/* Right Grid that takes the remaining space */}
                <Grid item sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }} component={Paper} elevation={6}>
                    {selectedCourseOffering != null ? (
                        <CourseOfferingForm courseOfferingId={selectedCourseOffering.id} onSubmit={handleCourseOfferingFormSubmit} onDelete={handleDeleteCourseOffering} />
                    ) : null}
                </Grid>
            </Grid>
        </Container>
    );
}
